import { Injectable } from '@angular/core';
import { catchError, map, Observable, throwError } from 'rxjs';
import { WebService } from '../../core/services/webservices.service';
import { StorageService } from '../../core/services/storage.service';
import { Usuario } from '../../core/models/usuario';
import { Response } from '../../core/models/response';
import { UsuarioDTO } from '../../core/models/usuarioDTO';
import { environment } from '../../../environments/environment';
import { UsuarioLoginDto } from '../dto/usuarioLogin.dto';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  url_servidor = environment.URL_WEBSERVICES;
  intranetAuthBase = `${environment.URL_WEBSERVICES}/intranet/auth`;

  constructor(
    private webservices: WebService,
    private storageService: StorageService
  ) {}

  login(usuario: UsuarioLoginDto): Observable<Response> {
    const url = this.intranetAuthBase + '/login';

    return this.webservices.peticionPost(url, usuario, false).pipe(
      map((respuesta: Response) => {
        const usuarioActualizado = this.persistirUsuarioDesdeRespuesta(respuesta);

        if (respuesta.isSuccess && usuarioActualizado) {
          respuesta.value = usuarioActualizado;
        }

        return respuesta;
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  refreshToken(){
    const url = this.intranetAuthBase + '/renovarToken';
    return this.webservices.peticionPost(url,null,true).pipe(
      map((respuesta:Response) => {
        const usuarioActualizado = this.persistirUsuarioDesdeRespuesta(respuesta);

        if (respuesta.isSuccess && usuarioActualizado) {
          respuesta.value = usuarioActualizado;
        }

        return respuesta;
      }),
      catchError((error) =>{
        return throwError(() => error);
      })
    )
  }

  ClaveTemporal(usuario: UsuarioDTO) {
    const url = this.intranetAuthBase + '/cambiarClave';

    return this.webservices.peticionPost(url, usuario, false).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  obtenerPermisos(): Observable<Response> {
    const url = this.intranetAuthBase + '/obtenerPermisos';

    return this.webservices.peticionPost(url,null,true).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  isAuthenticated(): boolean {
    const usuario = this.storageService.storageGet<Usuario>('usuario');
    return !!usuario?.token;
  }

  recuperarClave(userName: string): Observable<Response> {
    const url = this.intranetAuthBase + '/recuperarClave';

    const data = {
      userName,
    };
    return this.webservices.peticionPost(url, data, false).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  obtenerPerfil(): Observable<Response> {
    return this.webservices.peticionPost(this.intranetAuthBase + '/perfil', {}, true);
  }

  actualizarPerfil(datos: { nombres: string }): Observable<Response> {
    return this.webservices.peticionPost(this.intranetAuthBase + '/perfil/actualizar', datos, true);
  }

  cambiarClavePerfil(datos: { passwordActual: string; passwordNuevo: string }): Observable<Response> {
    return this.webservices.peticionPost(this.intranetAuthBase + '/perfil/cambiarClave', datos, true);
  }

  cerrarSesion(): Observable<Response> {
    return this.webservices.peticionPost(this.intranetAuthBase + '/cerrarSesion', {}, true);
  }

  private persistirUsuarioDesdeRespuesta(respuesta: Response): Usuario | null {
    if (!respuesta?.isSuccess) {
      return null;
    }

    const payload = this.normalizarPayloadAuth(respuesta);
    if (!payload) {
      return null;
    }

    const usuarioActual = this.storageService.storageGet('usuario') as Usuario | null;
    const usuarioActualizado: Usuario = {
      ...(usuarioActual ?? {}),
      ...payload,
    };

    if (!usuarioActualizado.token) {
      return null;
    }

    this.storageService.storageSet('usuario', JSON.stringify(usuarioActualizado));
    return usuarioActualizado;
  }

  private normalizarPayloadAuth(respuesta: Response): Partial<Usuario> | null {
    const candidatos = [respuesta.value, respuesta];

    for (const candidato of candidatos) {
      const payload = this.parsearPayload(candidato);
      if (payload?.token) {
        return payload;
      }
    }

    return null;
  }

  private parsearPayload(payload: unknown): Partial<Usuario> | null {
    if (!payload) {
      return null;
    }

    if (typeof payload === 'string') {
      try {
        const parseado = JSON.parse(payload);
        return typeof parseado === 'object' && parseado !== null
          ? (parseado as Partial<Usuario>)
          : null;
      } catch {
        return null;
      }
    }

    if (typeof payload === 'object') {
      return payload as Partial<Usuario>;
    }

    return null;
  }
}
